import {Suspense} from 'react';
import {Trans} from 'react-i18next';
import {diApiEu} from '@di-shared/api/di';
import {ERROR} from '@di-shared/const';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import SafeDrivingScoreDetailGraph from '@di-shared/features/safe-driving-LN/report/SafeDrivingScoreDetailGraph';
import {
  SafeDrivingScoreDetailGraphEuLoading,
  SafeDrivingScoreDetailGraphEuError,
} from '@di-shared/features/safe-driving-LN/report/SafeDrivingScoreDetailGraph/.eu';
import useCldrDatetimeFormatter from '@di-shared/utils/useCldrDatetimeFormatter';
import {parseDateWithFormat, subDate} from '@shared/dateUtils';
import {getQueryKey} from '@shared/queryKeyMap';
import {useSuspenseQuery} from '@tanstack/react-query';

export interface SafeDrivingScoreDetailsEuProps {
  vin: string;
  selectedMonth: string;
}

enum DescriptionType {
  SINGLE_GRAPH,
  COMPARISON_INCREASE,
  CONPARISON_DECREASE,
  COMPARISON_REMAIN,
}

function getDescriptionType(isSingleGraph: boolean, scoreBefore: number, scoreAfter: number) {
  if (isSingleGraph) {
    return DescriptionType.SINGLE_GRAPH;
  }
  if (scoreBefore > scoreAfter) {
    return DescriptionType.CONPARISON_DECREASE;
  }
  if (scoreAfter > scoreBefore) {
    return DescriptionType.COMPARISON_INCREASE;
  }
  return DescriptionType.COMPARISON_REMAIN;
}

export default function ({selectedMonth, vin}: SafeDrivingScoreDetailsEuProps) {
  return (
    <Suspense fallback={<SafeDrivingScoreDetailLoading />}>
      <Inner
        vin={vin}
        selectedMonth={selectedMonth}
      />
    </Suspense>
  );
}

function Inner({selectedMonth, vin}: SafeDrivingScoreDetailsEuProps) {
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const {data: monthlyTrendData} = useSuspenseQuery({
    queryKey: [getQueryKey(diApiEu.safeDriving.scoreByMonth1), vin, selectedMonth],
    queryFn: () => diApiEu.safeDriving.scoreByMonth1({vin, month: selectedMonth}),
    select: ({data}) => data,
  });

  const descriptionType = getDescriptionType(
    monthlyTrendData.scorePreviousMonth.score === 0,
    monthlyTrendData.scorePreviousMonth.score,
    monthlyTrendData.scoreCurrentMonth.score
  );

  const parsedDate = parseDateWithFormat(selectedMonth);
  if (!parsedDate) {
    return <></>;
  }

  return (
    <div className="section_container safety_driving_score_detail">
      <div className="section_container_header">
        <h4 className="body_xlarge_b">{t('ctn_dss_LN_driving_safety_score_details')}</h4>
      </div>
      {descriptionType === DescriptionType.SINGLE_GRAPH && (
        <p
          className="section_container_desc"
          style={{whiteSpace: 'pre-wrap'}}
        >
          {t('msg_dss_LN_your_score_is_now_n_$score', {score: monthlyTrendData.scoreCurrentMonth.score})}
        </p>
      )}
      {descriptionType === DescriptionType.COMPARISON_INCREASE && (
        <p className="section_container_desc">
          <Trans
            i18nKey="msg_dss_LN_increased_by_n_$score"
            values={{
              score: Math.abs(monthlyTrendData.scoreComparison.score),
            }}
            components={{
              Point: <span className="num" />,
              Icon: <span className="ic ic_score_up" />,
            }}
          />
        </p>
      )}
      {descriptionType === DescriptionType.CONPARISON_DECREASE && (
        <p className="section_container_desc">
          <Trans
            i18nKey="msg_dss_LN_decreased_by_n_$score"
            values={{
              score: Math.abs(monthlyTrendData.scoreComparison.score),
            }}
            components={{
              Point: <span className="num" />,
              Icon: <span className="ic ic_score_down" />,
            }}
          />
        </p>
      )}
      {descriptionType === DescriptionType.COMPARISON_REMAIN && (
        <p
          className="section_container_desc"
          style={{whiteSpace: 'pre-wrap'}}
        >
          {t('msg_dss_LN_remains_the_same')}
        </p>
      )}
      <SafeDrivingScoreDetailGraph
        key={selectedMonth}
        data={
          monthlyTrendData.scorePreviousMonth.score > 0
            ? [
                {
                  score: monthlyTrendData.scorePreviousMonth.score,
                  monthName: CldrDatetimeFormatter.format(subDate(parsedDate, {months: 1}), 'MMM'),
                },
                {
                  score: monthlyTrendData.scoreCurrentMonth.score,
                  monthName: CldrDatetimeFormatter.format(parsedDate, 'MMM'),
                },
              ]
            : [
                {
                  score: monthlyTrendData.scoreCurrentMonth.score,
                  monthName: CldrDatetimeFormatter.format(parsedDate, 'MMM'),
                },
              ]
        }
      />

      <div className="score_detail">
        <p className="score_title">{t('ctn_dss_LN_driving_safety_sub_scores')}</p>

        <div className="score_table">
          <table role="table">
            <caption>
              <span className="blind">{t('ctn_dss_LN_driving_safety_score_details')}</span>
            </caption>
            <colgroup>
              <col style={{width: '60%'}} />
              <col style={{width: '62px'}} />
              <col style={{width: '28%'}} />
              <col style={{width: '62px'}} />
            </colgroup>
            <thead>
              <tr>
                {monthlyTrendData.scorePreviousMonth.score === 0 ? (
                  <>
                    <th scope="col">
                      <span className="title"></span>
                    </th>
                    <th scope="col">
                      <span className="title"></span>
                    </th>
                    <th scope="col">
                      <span className="title">{t('ctn_dss_LN_selected_month')}</span>
                    </th>
                    <th scope="col">
                      <span className="title"></span>
                    </th>
                  </>
                ) : (
                  <>
                    <th scope="col">
                      <span className="title"></span>
                    </th>
                    <th scope="col">
                      <span className="title">{t('ctn_dss_LN_previous_month')}</span>
                    </th>
                    <th scope="col">
                      <span className="title">
                        <span className="blind">{t('ctn_dss_arrow_area')}</span>
                      </span>
                    </th>
                    <th scope="col">
                      <span className="title">{t('ctn_dss_LN_selected_month')}</span>
                    </th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              <DrivingScoreSubScoreItem
                title={t('ctn_dss_LN_estimated_annual_driving_distance')}
                beforeScore={monthlyTrendData.scorePreviousMonth.subScoreDetailResponse.estimatedAnnualMileage}
                afterScore={monthlyTrendData.scoreCurrentMonth.subScoreDetailResponse.estimatedAnnualMileage}
                compareScore={monthlyTrendData.scoreComparison.subScoreDetailResponse.estimatedAnnualMileage}
              />
              <DrivingScoreSubScoreItem
                title={t('ctn_dss_LN_hard_braking')}
                beforeScore={monthlyTrendData.scorePreviousMonth.subScoreDetailResponse.hardBraking}
                afterScore={monthlyTrendData.scoreCurrentMonth.subScoreDetailResponse.hardBraking}
                compareScore={monthlyTrendData.scoreComparison.subScoreDetailResponse.hardBraking}
              />
              <DrivingScoreSubScoreItem
                title={t('ctn_dss_LN_average_speed')}
                beforeScore={monthlyTrendData.scorePreviousMonth.subScoreDetailResponse.averageTripSpeed}
                afterScore={monthlyTrendData.scoreCurrentMonth.subScoreDetailResponse.averageTripSpeed}
                compareScore={monthlyTrendData.scoreComparison.subScoreDetailResponse.averageTripSpeed}
              />
              <DrivingScoreSubScoreItem
                title={t('ctn_dss_LN_maximum_speed')}
                beforeScore={monthlyTrendData.scorePreviousMonth.subScoreDetailResponse.maximumHighSpeed}
                afterScore={monthlyTrendData.scoreCurrentMonth.subScoreDetailResponse.maximumHighSpeed}
                compareScore={monthlyTrendData.scoreComparison.subScoreDetailResponse.maximumHighSpeed}
              />
              <DrivingScoreSubScoreItem
                title={t('ctn_dss_LN_driving_hours')}
                beforeScore={monthlyTrendData.scorePreviousMonth.subScoreDetailResponse.drivingTimeOfDay}
                afterScore={monthlyTrendData.scoreCurrentMonth.subScoreDetailResponse.drivingTimeOfDay}
                compareScore={monthlyTrendData.scoreComparison.subScoreDetailResponse.drivingTimeOfDay}
              />
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
function DrivingScoreSubScoreItem({
  title,
  beforeScore,
  afterScore,
  compareScore,
}: {
  title: string;
  beforeScore: number;
  afterScore: number;
  compareScore: number;
}) {
  const {t} = useInferableTranslation();

  if (beforeScore === 0) {
    return (
      <tr>
        <th
          scope="row"
          style={{whiteSpace: 'pre-wrap'}}
        >
          <span className="title">{title}</span>
        </th>
        <td>
          <span className="score_content"></span>
        </td>
        <td>
          <span className="score_content">{afterScore}</span>
        </td>
        <td>
          <span className="score_content"></span>
        </td>
      </tr>
    );
  }

  return (
    <tr>
      <th
        scope="row"
        style={{whiteSpace: 'pre-wrap'}}
      >
        <span className="title">{title}</span>
      </th>
      <td>
        <span className="score_content">{beforeScore}</span>
      </td>
      <td>
        <span className="score_content">
          <span className="ic ic_di_arrow">
            <span className="blind">{t('ctn_dss_right_area')}</span>
          </span>
        </span>
      </td>
      <td>
        <span className="score_content">
          {afterScore}
          {compareScore !== 0 && (
            <span className="badge badge_basic_container">
              {beforeScore < afterScore ? (
                <>
                  <span className="ic ic_score_up"></span>
                  {Math.abs(compareScore)}
                </>
              ) : (
                <>
                  <span className="ic ic_score_down"></span>
                  {Math.abs(compareScore)}
                </>
              )}
            </span>
          )}
        </span>
      </td>
    </tr>
  );
}

export function SafeDrivingScoreDetailLoading() {
  const {t} = useInferableTranslation();

  return (
    <div className="section_container safety_driving_score_detail">
      <div className="section_container_header skeleton">
        <h4 className="body_xlarge_b">{t('ctn_dss_LN_driving_safety_score_details')}</h4>
      </div>
      <p
        className="section_container_desc skeleton"
        style={{whiteSpace: 'pre-wrap', paddingLeft: '5px'}}
      >
        {t('msg_dss_LN_increased_by_n_$score')}

        <br />
      </p>
      <SafeDrivingScoreDetailGraphEuLoading />

      <div className="score_detail skeleton">
        <p className="score_title">{t('ctn_dss_LN_driving_safety_sub_scores')}</p>

        <div className="score_table">
          <table role="table">
            <caption>
              <span className="blind">{t('ctn_dss_LN_driving_safety_score_details')}</span>
            </caption>
            <colgroup>
              <col style={{width: '60%'}} />
              <col style={{width: '62px'}} />
              <col style={{width: '28%'}} />
              <col style={{width: '62px'}} />
            </colgroup>
            <thead>
              <tr>
                <th scope="col">
                  <span className="title"></span>
                </th>
                <th scope="col">
                  <span className="title">{t('ctn_dss_LN_previous_month')}</span>
                </th>
                <th scope="col">
                  <span className="title">
                    <span className="blind">{t('ctn_dss_arrow_area')}</span>
                  </span>
                </th>
                <th scope="col">
                  <span className="title">{t('ctn_dss_LN_selected_month')}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              <DrivingScoreSubScoreItem
                title={t('ctn_dss_LN_estimated_annual_driving_distance')}
                beforeScore={99}
                afterScore={99}
                compareScore={99}
              />
              <DrivingScoreSubScoreItem
                title={t('ctn_dss_LN_hard_braking')}
                beforeScore={99}
                afterScore={99}
                compareScore={99}
              />
              <DrivingScoreSubScoreItem
                title={t('ctn_dss_LN_average_speed')}
                beforeScore={99}
                afterScore={99}
                compareScore={99}
              />
              <DrivingScoreSubScoreItem
                title={t('ctn_dss_LN_maximum_speed')}
                beforeScore={99}
                afterScore={99}
                compareScore={99}
              />
              <DrivingScoreSubScoreItem
                title={t('ctn_dss_LN_driving_hours')}
                beforeScore={99}
                afterScore={99}
                compareScore={99}
              />
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export function SafeDrivingScoreDetailsNoData() {
  const {t} = useInferableTranslation();

  return (
    <div className="section_container safety_driving_score_detail">
      <div className="section_container_header">
        <h4 className="body_xlarge_b">{t('ctn_dss_LN_driving_safety_score_details')}</h4>
      </div>
      <p className="section_container_desc">{t('msg_dss_LN_no_driving_history')}</p>
      <SafeDrivingScoreDetailGraphEuError />

      <div className="score_detail">
        <p className="score_title">{t('ctn_dss_LN_driving_safety_sub_scores')}</p>
        <div className="score_table">
          <table role="table">
            <caption>
              <span className="blind">{t('ctn_dss_LN_driving_safety_score_details')}</span>
            </caption>
            <colgroup>
              <col style={{width: '60%'}} />
              <col style={{width: '62px'}} />
              <col style={{width: '28%'}} />
              <col style={{width: '62px'}} />
            </colgroup>
            <thead>
              <tr>
                <th scope="col">
                  <span className="title"></span>
                </th>
                <th scope="col">
                  <span className="title"></span>
                </th>
                <th scope="col">
                  <span className="title">{t('ctn_dss_LN_selected_month')}</span>
                </th>
                <th scope="col">
                  <span className="title"></span>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <th scope="row">
                  <span className="title">{t('ctn_dss_LN_estimated_annual_driving_distance')}</span>
                </th>
                <td>
                  <span className="score_content"></span>
                </td>
                <td>
                  <span className="score_content">{ERROR.DASH}</span>
                </td>
                <td>
                  <span className="score_content"></span>
                </td>
              </tr>
              <tr>
                <th scope="row">
                  <span className="title">{t('ctn_dss_LN_hard_braking')}</span>
                </th>
                <td>
                  <span className="score_content"></span>
                </td>
                <td>
                  <span className="score_content">{ERROR.DASH}</span>
                </td>
                <td>
                  <span className="score_content"></span>
                </td>
              </tr>
              <tr>
                <th
                  scope="row"
                  style={{whiteSpace: 'pre-wrap'}}
                >
                  <span className="title">{t('ctn_dss_LN_average_speed')}</span>
                </th>
                <td>
                  <span className="score_content"></span>
                </td>
                <td>
                  <span className="score_content">{ERROR.DASH}</span>
                </td>
                <td>
                  <span className="score_content"></span>
                </td>
              </tr>
              <tr>
                <th
                  scope="row"
                  style={{whiteSpace: 'pre-wrap'}}
                >
                  <span className="title">{t('ctn_dss_LN_maximum_speed')}</span>
                </th>
                <td>
                  <span className="score_content"></span>
                </td>
                <td>
                  <span className="score_content">{ERROR.DASH}</span>
                </td>
                <td>
                  <span className="score_content"></span>
                </td>
              </tr>
              <tr>
                <th
                  scope="row"
                  style={{whiteSpace: 'pre-wrap'}}
                >
                  <span className="title">{t('ctn_dss_LN_driving_hours')}</span>
                </th>
                <td>
                  <span className="score_content"></span>
                </td>
                <td>
                  <span className="score_content">{ERROR.DASH}</span>
                </td>
                <td>
                  <span className="score_content"></span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
